<template>
  <div class="app-modal-field full-width" data-focus-field="customers">
    <div
      v-for="(customerOption, index) in customerSelectors"
      :key="`customer-selector-${index}`"
      class="multi-customer-selector-row"
    >
      <AppInlineMultiCustomerSelector
        :model-value="customerOption"
        label="Cliente *"
        placeholder="Buscar cliente"
        :show-new-customer-button="isLastSelector(index)"
        @update:model-value="updateCustomerSelector(index, $event)"
        @customer-created="prependCreatedCustomerSelector(index, $event)"
      />

      <div class="multi-customer-selector-row__actions">
        <button
          v-if="isLastSelector(index)"
          type="button"
          class="multi-customer-selector-row__btn multi-customer-selector-row__btn--add"
          title="Agregar cliente"
          @click="appendCustomerSelector"
        >
          <i class="fa fa-plus" aria-hidden="true"></i>
        </button>

        <button
          v-else
          type="button"
          class="multi-customer-selector-row__btn multi-customer-selector-row__btn--remove"
          title="Eliminar selector"
          @click="removeCustomerSelector(index)"
        >
          <i class="fa fa-trash" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <transition name="inline-warning-fade">
      <p v-if="duplicateCustomerWarning.visible" class="multi-customer-selector-warning" role="status">
        {{ duplicateCustomerWarning.message }}
      </p>
    </transition>

    <div class="invalid-feedback d-block" v-if="hasCustomersError">
      <span>{{ customersErrorMessage }}</span>
    </div>
  </div>
</template>

<script>
import AppInlineMultiCustomerSelector from '@/components/common/selectors/AppInlineMultiCustomerSelector.vue';

export default {
  name: 'SaleModalMultiCustomerField',
  components: {
    AppInlineMultiCustomerSelector,
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
    v$: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      duplicateCustomerWarning: {
        visible: false,
        message: 'No se pueden repetir clientes en un mismo negocio.',
        timeoutId: null,
      },
    };
  },
  computed: {
    customerSelectors() {
      if (!Array.isArray(this.form.customers)) {
        this.form.customers = [];
      }

      if (this.form.customers.length === 0) {
        this.form.customers.push(null);
      }

      return this.form.customers;
    },
    hasCustomersError() {
      return Boolean(this.v$?.form?.customers?.$error);
    },
    customersErrorMessage() {
      const firstError = this.v$?.form?.customers?.$errors?.[0];
      return firstError?.$message || 'Selecciona al menos un cliente.';
    },
  },
  methods: {
    resolveCustomerOptionId(value) {
      const rawId = value?.value || value?.id || value || null;
      const normalizedId = String(rawId || '').trim();
      return normalizedId === '' ? null : normalizedId;
    },
    syncPrimaryCustomer() {
      const firstSelected = this.customerSelectors.find((item) => this.resolveCustomerOptionId(item));
      this.form.customer = firstSelected || null;
    },
    showDuplicateCustomerWarning() {
      if (this.duplicateCustomerWarning.timeoutId) {
        clearTimeout(this.duplicateCustomerWarning.timeoutId);
      }

      this.duplicateCustomerWarning.visible = true;
      this.duplicateCustomerWarning.timeoutId = setTimeout(() => {
        this.duplicateCustomerWarning.visible = false;
        this.duplicateCustomerWarning.timeoutId = null;
      }, 2400);
    },
    hasDuplicateCustomerInOtherSelector(index, value) {
      const candidateId = this.resolveCustomerOptionId(value);
      if (!candidateId) {
        return false;
      }

      return this.customerSelectors.some((selectedOption, selectedIndex) => {
        if (selectedIndex === index) {
          return false;
        }

        return this.resolveCustomerOptionId(selectedOption) === candidateId;
      });
    },
    isLastSelector(index) {
      return index === this.customerSelectors.length - 1;
    },
    appendCustomerSelector() {
      this.form.customers.push(null);
      this.syncPrimaryCustomer();
    },
    removeCustomerSelector(index) {
      if (this.customerSelectors.length <= 1) {
        this.form.customers = [null];
        this.syncPrimaryCustomer();
        return;
      }

      this.form.customers.splice(index, 1);
      this.syncPrimaryCustomer();
    },
    updateCustomerSelector(index, value) {
      if (this.hasDuplicateCustomerInOtherSelector(index, value)) {
        this.showDuplicateCustomerWarning();
        return;
      }

      this.form.customers.splice(index, 1, value || null);
      this.syncPrimaryCustomer();
    },
    prependCreatedCustomerSelector(sourceIndex, createdCustomer) {
      if (sourceIndex >= 0 && sourceIndex < this.form.customers.length) {
        this.form.customers.splice(sourceIndex, 1, null);
      }

      this.form.customers.unshift(createdCustomer || null);
      this.syncPrimaryCustomer();
    },
  },
  beforeUnmount() {
    if (this.duplicateCustomerWarning.timeoutId) {
      clearTimeout(this.duplicateCustomerWarning.timeoutId);
      this.duplicateCustomerWarning.timeoutId = null;
    }
  },
};
</script>

<style scoped>
.multi-customer-selector-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

.multi-customer-selector-row :deep(.app-inline-customer-selector) {
  flex: 1;
  min-width: 0;
}

.multi-customer-selector-row:last-child {
  margin-bottom: 0;
}

.multi-customer-selector-row__actions {
  margin-top: 29px;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.multi-customer-selector-warning {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.35;
  color: #b45309;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.35);
  border-radius: 6px;
  padding: 6px 8px;
}

.inline-warning-fade-enter-active,
.inline-warning-fade-leave-active {
  transition: opacity 0.18s ease;
}

.inline-warning-fade-enter-from,
.inline-warning-fade-leave-to {
  opacity: 0;
}

.multi-customer-selector-row__btn {
  width: 34px;
  height: 34px;
  border: 1px solid rgba(153, 152, 153, 0.35);
  border-radius: 7px;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.multi-customer-selector-row__btn--add {
  color: #0070cd;
}

.multi-customer-selector-row__btn--remove {
  color: #ef4444;
}
</style>
